import React, { useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import clsx from 'clsx';
import { usePopper } from 'react-popper';
import { Slot } from '@radix-ui/react-slot';

type Align = 'start' | 'center' | 'end';
type Side = 'top' | 'bottom' | 'left' | 'right';

type Placement =
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
  | 'top-start'
  | 'top-end'
  | 'bottom-start'
  | 'bottom-end'
  | 'left-start'
  | 'left-end'
  | 'right-start'
  | 'right-end';

interface DropdownMenuContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerRef: React.MutableRefObject<HTMLElement | null>;
  contentId: string;
}

const DropdownMenuContext = React.createContext<DropdownMenuContextValue | null>(null);

function useDropdownMenuContext(component: string): DropdownMenuContextValue {
  const context = useContext(DropdownMenuContext);
  if (!context) {
    throw new Error(`${component} must be used within <DropdownMenu>.`);
  }
  return context;
}

function getPlacement(side: Side, align: Align): Placement {
  if (align === 'center') return side;
  return `${side}-${align}` as Placement;
}

export interface DropdownMenuProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  open,
  defaultOpen = false,
  onOpenChange,
  children,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = open !== undefined;
  const currentOpen = isControlled ? open : internalOpen;
  const triggerRef = useRef<HTMLElement | null>(null);
  const contentId = useId();

  const setOpen = useCallback((next: boolean) => {
    if (!isControlled) {
      setInternalOpen(next);
    }
    onOpenChange?.(next);
  }, [isControlled, onOpenChange]);

  const value = useMemo(
    () => ({ open: currentOpen, setOpen, triggerRef, contentId }),
    [contentId, currentOpen, setOpen]
  );

  return (
    <DropdownMenuContext.Provider value={value}>
      {children}
    </DropdownMenuContext.Provider>
  );
};

export interface DropdownMenuTriggerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  asChild?: boolean;
}

export const DropdownMenuTrigger = React.forwardRef<HTMLElement, DropdownMenuTriggerProps>(
  ({ asChild = false, onClick, className, children, ...props }, ref) => {
    const { open, setOpen, triggerRef, contentId } = useDropdownMenuContext('DropdownMenuTrigger');

    const setRefs = (node: HTMLElement | null) => {
      triggerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLElement | null>).current = node;
    };

    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        ref={setRefs}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          onClick?.(event);
          if (event.defaultPrevented) return;
          setOpen(!open);
        }}
        className={className}
        {...props}
      >
        {children}
      </Comp>
    );
  }
);

DropdownMenuTrigger.displayName = 'DropdownMenuTrigger';

export interface DropdownMenuContentProps {
  align?: Align;
  side?: Side;
  sideOffset?: number;
  alignOffset?: number;
  className?: string;
  children: React.ReactNode;
  portal?: boolean;
  portalContainer?: HTMLElement | null;
}

export const DropdownMenuContent: React.FC<DropdownMenuContentProps> = ({
  align = 'start',
  side = 'bottom',
  sideOffset = 8,
  alignOffset = 0,
  className,
  children,
  portal = true,
  portalContainer,
}) => {
  const { open, setOpen, triggerRef, contentId } = useDropdownMenuContext('DropdownMenuContent');
  const [menuElement, setMenuElement] = useState<HTMLDivElement | null>(null);

  const placement = getPlacement(side, align);
  const { styles, attributes } = usePopper(triggerRef.current, menuElement, {
    placement,
    modifiers: [
      { name: 'offset', options: { offset: [alignOffset, sideOffset] } },
      { name: 'preventOverflow', options: { padding: 8 } },
      { name: 'flip', options: { padding: 8 } },
    ],
  });

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuElement?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [open, setOpen, triggerRef, menuElement]);

  if (!open) return null;

  const menu = (
    <div
      ref={setMenuElement}
      style={styles.popper}
      {...attributes.popper}
      id={contentId}
      role="menu"
      className="z-[1001]"
    >
      <div
        className={clsx(
          'min-w-[180px] rounded-lg border-2 border-black bg-white p-1 shadow-[4px_4px_0px_0px_rgb(17,24,39)]',
          '[.theme-liquid-glass_&]:border-white/20 [.theme-liquid-glass_&]:bg-slate-800/90',
          '[.theme-liquid-glass_&]:backdrop-blur-xl [.theme-liquid-glass_&]:shadow-none',
          className
        )}
      >
        {children}
      </div>
    </div>
  );

  if (!portal) return menu;
  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(menu, portalContainer ?? document.body);
};

export interface DropdownMenuItemProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  inset?: boolean;
  disabled?: boolean;
  closeOnSelect?: boolean;
  onSelect?: () => void;
}

export const DropdownMenuItem = React.forwardRef<HTMLButtonElement, DropdownMenuItemProps>(
  ({
    inset = false,
    disabled = false,
    closeOnSelect = true,
    onClick,
    onSelect,
    className,
    children,
    ...props
  }, ref) => {
    const { setOpen } = useDropdownMenuContext('DropdownMenuItem');

    return (
      <button
        ref={ref}
        type="button"
        role="menuitem"
        disabled={disabled}
        aria-disabled={disabled}
        onClick={(event) => {
          onClick?.(event);
          if (event.defaultPrevented || disabled) return;
          onSelect?.();
          if (closeOnSelect) setOpen(false);
        }}
        className={clsx(
          'w-full text-left text-sm font-medium px-2.5 py-2 rounded-md transition-colors',
          inset && 'pl-8',
          disabled
            ? 'opacity-50 cursor-not-allowed text-slate-500'
            : 'text-slate-800 hover:bg-slate-100',
          '[.theme-liquid-glass_&]:text-white/90 [.theme-liquid-glass_&]:hover:bg-white/10',
          className
        )}
        {...props}
      >
        {children}
      </button>
    );
  }
);

DropdownMenuItem.displayName = 'DropdownMenuItem';

export const DropdownMenuLabel: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => (
  <div
    role="presentation"
    className={clsx(
      'px-2.5 py-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500',
      '[.theme-liquid-glass_&]:text-white/60',
      className
    )}
    {...props}
  />
);

export const DropdownMenuSeparator: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({
  className,
  ...props
}) => (
  <div
    role="separator"
    className={clsx(
      'my-1 h-px bg-slate-200',
      '[.theme-liquid-glass_&]:bg-white/10',
      className
    )}
    {...props}
  />
);
